import React from "react";
import { motion } from "framer-motion";
import { Upload, Megaphone, Wallet } from "lucide-react";

const STEPS = [
    {
        n: "01",
        icon: Upload,
        title: "Upload your music",
        desc: "Drag in your masters. Pick stores. Set release date. We handle metadata, ISRCs and delivery to 150+ platforms.",
    },
    {
        n: "02",
        icon: Megaphone,
        title: "Grow your audience",
        desc: "Playlist pitching, smart links, fan-targeted ads and editorial submissions — all from one cockpit.",
    },
    {
        n: "03",
        icon: Wallet,
        title: "Monetize globally",
        desc: "Track every stream, split royalties automatically.",
    },
];

export const HowItWorks = () => {
    return (
        <section
            id="how"
            className="relative py-28 lg:py-36 overflow-hidden"
            data-testid="how-section"
        >
            <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#F54815] opacity-10 blur-[140px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 relative">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="max-w-3xl mb-16 lg:mb-24"
                >
                    <p className="text-sm uppercase tracking-[0.3em] text-[#F54815] mb-4 font-medium">
                        How it works
                    </p>
                    <h2
                        className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]"
                        data-testid="how-heading"
                    >
                        From bedroom to billboard
                        <br />
                        in three <span className="text-[#791EE2]">simple steps</span>.
                    </h2>
                </motion.div>

                <div className="relative">
                    {/* vertical connector line */}
                    <div className="hidden md:block absolute left-0 right-0 top-12 h-px bg-gradient-to-r from-transparent via-[#791EE2]/40 to-transparent" />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 lg:gap-12">
                        {STEPS.map((s, i) => (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{
                                    duration: 0.6,
                                    delay: i * 0.15,
                                }}
                                className="relative"
                                data-testid={`step-${i}`}
                            >
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="relative">
                                        <div className="w-24 h-24 rounded-full border border-white/10 bg-[#030023] flex items-center justify-center relative z-10">
                                            <s.icon
                                                size={28}
                                                strokeWidth={1.5}
                                                className="text-[#791EE2]"
                                            />
                                        </div>
                                        <div className="absolute inset-0 w-24 h-24 rounded-full bg-[#791EE2] opacity-30 blur-2xl pulse-glow" />
                                    </div>
                                </div>

                                <div className="font-heading text-7xl lg:text-8xl font-extrabold tracking-tighter text-white/10 mb-2 leading-none">
                                    {s.n}
                                </div>
                                <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white tracking-tight mb-4">
                                    {s.title}
                                </h3>
                                <p className="text-white/55 font-body leading-relaxed max-w-sm">
                                    {s.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
